// Legal/company page content now lives in the DB (GLOBAL `legal_documents`, edited by the platform
// admin at Platform console → Legal Pages) and is served at `/api/public/legal/:slug`. LegalPage.js
// fetches + renders it. This file only holds fallback titles shown in the header/tab while the doc
// loads (or if the fetch fails), so the page shell never appears blank.
export const LEGAL_TITLES = {
  "about": "About Vidyapeeth360 — an India-first school ERP company",
  "privacy-policy": "Privacy Policy",
  "terms-and-conditions": "Terms of Service",
  "refund-policy": "Refund & Cancellation Policy",
  "cookie-policy": "Cookie Policy",
};

export const LEGAL_SLUGS = Object.keys(LEGAL_TITLES);

export const LEGAL_PATHS = Object.freeze({
  about: "/about",
  "privacy-policy": "/privacy-policy",
  "terms-and-conditions": "/terms-and-conditions",
  "refund-policy": "/refund-policy",
  "cookie-policy": "/cookie-policy",
});

export const LEGAL_SEO = Object.freeze({
  about: Object.freeze({
    // SEO-11: name the company and the category ("India-first school ERP company") instead of
    // the generic "About Us — Our Mission". Byte-mirrored in prerender ROUTE_META["/about"].
    title: "About Vidyapeeth360 — India-First School ERP Company",
    description: "Who builds Vidyapeeth360 — an India-first school ERP company with documented workflows, transparent pricing in ₹ and governed AI assistance.",
  }),
  "privacy-policy": Object.freeze({
    title: "Privacy Policy | Vidyapeeth360",
    description: "How Vidyapeeth360 collects, uses and safeguards personal data, and how we support India's DPDP Act 2023 framework — rights, consent, retention and grievance redressal.",
  }),
  "terms-and-conditions": Object.freeze({
    title: "Terms of Service | Vidyapeeth360",
    description: "The terms that govern your use of Vidyapeeth360 — subscriptions, acceptable use, payments, data ownership and how we operate the service.",
  }),
  "refund-policy": Object.freeze({
    title: "Refund & Cancellation Policy | Vidyapeeth360",
    description: "Vidyapeeth360's refund and cancellation policy — subscription billing, cancellation and refund eligibility, and how to raise a billing request.",
  }),
  "cookie-policy": Object.freeze({
    title: "Cookie Policy | Vidyapeeth360",
    description: "The cookies Vidyapeeth360 uses and why — essential sign-in cookies, device preferences, and analytics and session replay that load only with your consent. No advertising cookies.",
  }),
});

// Public legal copy remains CMS-owned. These compact, versioned continuity documents are rendered
// only when both the published endpoint and a previously cached published document are unavailable.
// They keep the essential policy route readable during an outage without pretending to supersede
// an administrator-authored publication. Successful CMS responses are always preferred and cached.
export const LEGAL_FALLBACK_VERSION = "legal-continuity-2026-08-18";
const CONTINUITY_DATE = "18 August 2026";
const OPERATOR = "HelpRevX Software Private Limited";

const continuityDoc = (slug, body) => Object.freeze({
  slug,
  title: LEGAL_TITLES[slug],
  effective_date: CONTINUITY_DATE,
  powered_by: OPERATOR,
  body,
  fallback_version: LEGAL_FALLBACK_VERSION,
  is_fallback: true,
});

export const LEGAL_FALLBACKS = Object.freeze({
  about: continuityDoc("about", `Vidyapeeth360 is a connected school-operations platform developed by HelpRevX Software Private Limited for Indian K–12 institutions.

## What we are building

The platform brings admissions, student records, attendance, fees, academics, people operations, transport, communication and governed assistance into connected, permission-aware workflows.

## How we work

- We document product scope and availability instead of implying that every workflow is automatic.
- Schools review their own board, provider, migration, policy and rollout requirements before go-live.
- Actions that affect school records remain subject to authorised roles, review and recorded evidence where the workflow supports it.

## Company contact

For product and commercial questions, write to sales@vidyapeeth360.com. For support, write to support@vidyapeeth360.com. Privacy questions can be sent to privacy@helprevx.com.`),
  "privacy-policy": continuityDoc("privacy-policy", `This continuity copy summarises how HelpRevX Software Private Limited handles personal data through Vidyapeeth360 when the current published policy cannot be retrieved. The published CMS document remains the authoritative version and will replace this copy automatically when service returns.

## Data and roles

Schools ordinarily determine why and how student, parent, staff and operational records are used in their institution. Vidyapeeth360 processes those records to provide the school workflows the institution configures. HelpRevX separately handles account, enquiry, billing, security and support data needed to operate the service.

## Purposes

Data may be used to provide authorised school workflows, secure accounts, process support and billing requests, maintain audit and security records, and improve service reliability. Vidyapeeth360 does not sell school records or use children's data for advertising or cross-site profiling.

## Sharing and providers

Data is disclosed only where needed to operate configured services, to an institution's selected integration provider, or where a lawful request requires it. A school should review provider and deployment details applicable to its rollout.

## Children and school responsibility

Schools remain responsible for appropriate notices, permissions and parent or lawful-guardian consent where required. Product controls can support authorised access, consent evidence and review; they do not replace the institution's legal duties.

## Retention, correction and deletion

Retention and deletion depend on the institution's instructions, applicable record-keeping duties, security needs and the governing agreement. Parents, students and staff should normally contact their school first to request access, correction or erasure. The public Account deletion page submits a verified review request; it does not promise immediate deletion of records that must be retained.

## Contact

Privacy requests: privacy@helprevx.com. Legal or data-processing questions: legal@helprevx.com. Product support: support@vidyapeeth360.com.`),
  "terms-and-conditions": continuityDoc("terms-and-conditions", `This continuity copy provides the essential service-use boundary while the current published Terms cannot be retrieved. The executed order form and the current CMS-published Terms govern an institution's subscription and will take precedence over this continuity copy.

## Accounts and authorised use

Users must provide accurate information, protect their credentials and use only the school tenant and records they are authorised to access. Schools control role assignments and are responsible for their users, uploaded records, notices, permissions and lawful instructions.

## Product scope and subscriptions

Available modules, limits, integrations, implementation work and charges depend on the selected plan and approved order. Public descriptions explain evaluation scope but do not create an entitlement. Optional providers and usage-based services may have separate terms or charges.

## Acceptable use

The service must not be used to violate law, another person's rights, security controls or institutional policy; introduce malicious code; probe another tenant; or attempt unauthorised access. Access may be restricted where necessary to protect users, data or the service.

## School data and service operation

The institution retains responsibility for its records. HelpRevX processes school data to operate configured services, provide support, maintain security and follow documented instructions, subject to applicable law and the governing agreement.

## Changes, cancellation and disputes

Plan changes, renewal, cancellation, export, retention, refunds, service commitments, liability and dispute terms are determined by the current published Terms, the order form and applicable law. Do not rely on this continuity copy for a commercial commitment.

## Contact

Commercial questions: sales@vidyapeeth360.com. Legal questions: legal@helprevx.com. Support: support@vidyapeeth360.com.`),
  "refund-policy": continuityDoc("refund-policy", `This continuity copy explains how to start a billing review while the current published Refund & Cancellation Policy cannot be retrieved. The executed order form and current CMS-published policy determine eligibility and take precedence.

## Before payment

Review the selected plan, student or usage basis, billing period, implementation scope, optional integrations, gateway charges and communication-service costs shown in the approved proposal or order form.

## Cancellation and refund review

A cancellation request does not by itself establish refund eligibility. Any refund is reviewed against the payment status, service period, work already delivered, activated integrations, usage-based charges, taxes, the governing order and applicable law.

## How to request a review

Write to sales@vidyapeeth360.com with the institution name, invoice or payment reference, request reason and an authorised contact. Do not include student records or payment credentials. Product-support questions should be sent to support@vidyapeeth360.com.

## Processing

The team will acknowledge the request through the supplied contact channel and communicate the applicable decision and next step after the account and order are reviewed. This continuity copy makes no automatic-refund, response-time or settlement-time promise.`),
  "cookie-policy": continuityDoc("cookie-policy", `This continuity copy explains the website's browser-storage categories while the current published Cookie Policy cannot be retrieved. The current CMS-published policy will replace it automatically when service returns.

## Necessary storage

Vidyapeeth360 uses security and session cookies for signed-in areas and local or session storage for choices a visitor requests, such as theme, language and cookie-consent preference. Necessary storage supports account security and requested site behaviour.

## Optional analytics

Optional public-site analytics remains off until a visitor provides the relevant consent. When enabled in a configured deployment, analytics is used to understand public-site performance and campaign attribution. Marketing forms must not place student records or direct contact details into analytics event properties.

## Your choice

Use Cookie settings in the footer to review or change optional analytics consent. Browser controls can also remove stored data, although removing necessary session data may sign a user out or reset preferences.

## Contact

Questions about website privacy or browser storage can be sent to privacy@helprevx.com.`),
});
